import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'mne-chunk-reload';

/** Sandt når fejlen skyldes en manglende/forældet JS-chunk efter en ny udrulning. */
export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg)
  );
}

/**
 * Som React.lazy, men genindlæser siden én gang hvis chunken mangler,
 * fordi der er udrullet en ny version. sessionStorage sikrer, at vi
 * aldrig kan havne i en genindlæsningsløkke.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // ignoreres
      }
      return mod;
    } catch (error) {
      if (isChunkLoadError(error)) {
        let alreadyTried = false;
        try {
          alreadyTried = sessionStorage.getItem(RELOAD_KEY) === '1';
          if (!alreadyTried) sessionStorage.setItem(RELOAD_KEY, '1');
        } catch {
          alreadyTried = true;
        }
        if (!alreadyTried) {
          window.location.reload();
          // Hold Suspense i gang mens siden genindlæses
          return await new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}
